import React from 'react';
import PropTypes from 'prop-types';
import { Redirect, useLocation } from 'react-router-dom';
import { Formik, Form } from 'formik';

import { SR_API_ACTION_PARTNER_SIGNUP } from 'api/actionTypes';

import { USER_ROLE_ANONYMOUS } from 'common/userRoles';

import { SR_PARTNER_SIGNUP_USER_IS_ALREADY_PARTNER_ERROR } from 'common/errorCodes';

import {
  partnerAgentSignupTypeOptionItems,
  PARTNER_AGENT_SIGNUP_TYPE_LISTING_AGENT,
  PARTNER_AGENT_SIGNUP_TYPE_OTHER,
  PARTNER_AGENT_SIGNUP_TYPE_SUNRISE,
  PARTNER_AGENT_SIGNUP_TYPE_TENANT_SCREENING,
  PARTNER_AGENT_SIGNUP_TYPE_UTILITY_PROFIT,
  PARTNER_SIGNUP_PROVIDER_SIGNUP_FORM,
} from 'common/partnerConstants';

import { initialApiActionState } from 'api/createApiClient';

import {
  createFormValidationFn,
  createInitialValues,
  hideFieldIf,
} from 'client/helpers/formikHelpers';

import { getUserLandingPage } from 'client/helpers/allowedRoles';

import useBranch from 'client/hooks/useBranch';
import AuthActions from 'client/actions/Auth';

import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrCard from 'client/components/SrCard/SrCard';
import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrLink from 'client/components/SrLink/SrLink';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrStaticFooter from 'client/components/SrStaticFooter';
import SrFooterSaveButton from 'client/components/SrFooterSaveButton';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';

const getShouldAskSunroomReference = (agentSignupReasonType) => {
  if (typeof agentSignupReasonType !== 'string') {
    return false;
  }

  const validAgentSignupReasons = [
    PARTNER_AGENT_SIGNUP_TYPE_TENANT_SCREENING,
    PARTNER_AGENT_SIGNUP_TYPE_SUNRISE,
    PARTNER_AGENT_SIGNUP_TYPE_LISTING_AGENT,
  ];

  return validAgentSignupReasons.includes(agentSignupReasonType);
};

const getFormFields = ({ salesReps = [], formValues, isUtilityProfitPage }) => {
  const shouldAskSunroomReference = getShouldAskSunroomReference(formValues.agentSignupReasonType);
  const formFields = [
    {
      id: 'firstName',
      title: 'First Name',
      type: 'short_text',
      validations: ['required'],
    },
    {
      id: 'lastName',
      title: 'Last Name',
      type: 'short_text',
      validations: ['required'],
    },
    {
      id: 'email',
      title: 'Email',
      type: 'email',
      validations: ['required', 'email'],
    },
    {
      id: 'phone',
      title: 'Phone',
      type: 'phone',
      validations: ['required', 'phone'],
    },
    {
      id: 'password',
      title: 'New Password',
      type: 'password',
      validations: ['required'],
    },
    {
      id: 'confirmPassword',
      title: 'Confirm Password',
      type: 'password',
      validations: ['required', 'confirmPassword:password'],
    },
    {
      id: 'managementCompanyName',
      title: 'What is the name of your property management company?',
      type: 'short_text',
      validations: ['required'],
    },
    {
      id: 'totalManagedDoors',
      title: 'How many doors do you have under management?',
      type: 'short_text',
      validations: ['required', 'nonNegativeNumber'],
      properties: {
        onlyAcceptNumbers: true,
        pattern: '[0-9]+',
      },
    },
    hideFieldIf(isUtilityProfitPage, {
      id: 'agentSignupReasonType',
      title: 'Why are you interested in using Sunroom?',
      type: 'select',
      properties: {
        choices: partnerAgentSignupTypeOptionItems.map((option) => ({
          value: option.value,
          label: option.label,
        })),
      },
      validations: ['required'],
    }),
    hideFieldIf(formValues.agentSignupReasonType !== PARTNER_AGENT_SIGNUP_TYPE_OTHER, {
      id: 'otherAgentSignupReasonProvided',
      title: 'Other reason:',
      type: 'short_text',
      validations: ['required'],
    }),
    hideFieldIf(salesReps.length < 1 || !shouldAskSunroomReference, {
      id: 'salesRepUserId',
      title: 'Who at Sunroom have you spoken to?',
      type: 'select',
      properties: {
        enableResetOption: true,
        placeholder: 'I haven’t spoken with anyone at Sunroom',
        choices: salesReps.map((salesRep) => ({
          value: salesRep.id,
          label: salesRep.name,
        })),
      },
    }),
  ];
  return formFields;
};

const SrPartnerSignUpForm = ({ navbarImage, pageHeader, pageSubtitle }) => {
  const {
    currentUser,
    isPartnerApp,
    salesReps,
    agentSignupApiState = initialApiActionState,
    dispatch,
  } = useBranch({
    currentUser: ['currentUser'],
    isPartnerApp: ['mobileApp', 'isPartnerApp'],
    salesReps: ['salesReps'],
    agentSignupApiState: ['apiState', SR_API_ACTION_PARTNER_SIGNUP, 'default'],
  });

  const location = useLocation();

  const isUtilityProfitPage = location.pathname.includes('utility-profit');

  const errorMessage =
    agentSignupApiState.error &&
    ((agentSignupApiState.error.constraint === 'users_phone_unique' &&
      'There is already an account with that phone number.') ||
      (agentSignupApiState.error.constraint === 'users_email_unique' &&
        'There is already an account with that email.') ||
      (agentSignupApiState.error.constraint === 'partners_slug_unique' &&
        'There is already an agent with that name.') ||
      agentSignupApiState.error.message);

  const _onSubmit = (formValues) => {
    const payload = {
      name: `${formValues.firstName} ${formValues.lastName}`,
      signupProvider: PARTNER_SIGNUP_PROVIDER_SIGNUP_FORM,
      ...formValues,
    };

    delete payload.firstName;
    delete payload.lastName;

    if (isUtilityProfitPage) {
      payload.agentSignupReasonType = PARTNER_AGENT_SIGNUP_TYPE_UTILITY_PROFIT;
    }

    dispatch(AuthActions.partnerSignup, payload);
  };

  if (!currentUser.roles.includes(USER_ROLE_ANONYMOUS)) {
    return (
      <Redirect
        to={getUserLandingPage({
          userRoles: currentUser.roles,
        })}
      />
    );
  }

  return (
    <SrPartnerPageV2
      backUrl={isPartnerApp ? '/login' : '/get-started'}
      navbarImage={navbarImage}
      pageHeader={pageHeader}
      pageSubtitle={pageSubtitle}
    >
      <SrCard>
        <Formik
          initialValues={createInitialValues({
            formFields: getFormFields({
              salesReps,
              formValues: {},
              isUtilityProfitPage,
            }),
          })}
          validate={(formValues) => {
            return createFormValidationFn(
              getFormFields({ salesReps, formValues, isUtilityProfitPage }),
            )(formValues);
          }}
          onSubmit={_onSubmit}
          render={(formikBag) => (
            <Form>
              {getFormFields({
                salesReps,
                formValues: formikBag.values,
                isUtilityProfitPage,
              }).map((formField, index) => (
                <SrFormikField
                  formField={formField}
                  formikBag={formikBag}
                  theme={SR_FIELD_THEME_ADOBE}
                  key={formField.id}
                  margin={index === 0 ? '0' : null}
                />
              ))}
              <SrStaticFooter>
                <SrText
                  display="block"
                  lineHeight="25px"
                  font="greycliff"
                  color="grey400"
                  fontSize="13px"
                  weight="500"
                >
                  1 min to complete
                </SrText>
                <SrFooterSaveButton
                  isSubmitting={agentSignupApiState.inProgress}
                  buttonText="Sign up"
                />
              </SrStaticFooter>
            </Form>
          )}
        />
      </SrCard>

      <SrBox marginTop="26px" textAlign="center">
        <SrText font="greycliff" weight="bold">
          Already have an account? &nbsp;&nbsp;
          <SrLink to={`/login${location.search}`} color="blue300">
            Log In &gt;
          </SrLink>
        </SrText>
      </SrBox>

      {errorMessage &&
        agentSignupApiState.error?.name !== SR_PARTNER_SIGNUP_USER_IS_ALREADY_PARTNER_ERROR && (
          <SrBox marginTop="10px" borderRadius="3px" overflow="hidden">
            <SrErrorMessage>{errorMessage}</SrErrorMessage>
          </SrBox>
        )}
      {agentSignupApiState.error?.name === SR_PARTNER_SIGNUP_USER_IS_ALREADY_PARTNER_ERROR && (
        <SrBox marginTop="10px" borderRadius="3px" overflow="hidden">
          <SrErrorMessage>
            {'It looks like you already have an account, please login '}
            <SrLink to={`/login${location.search}`}>
              <SrText weight="600" color="white" decoration="underline" size="14">
                here
              </SrText>
            </SrLink>
            .
          </SrErrorMessage>
        </SrBox>
      )}
    </SrPartnerPageV2>
  );
};

SrPartnerSignUpForm.propTypes = {
  navbarImage: PropTypes.string,
  pageHeader: PropTypes.string,
  pageSubtitle: PropTypes.string,
};

SrPartnerSignUpForm.displayName = 'SrPartnerSignUpForm';

export default SrPartnerSignUpForm;
